import type { i18n as I18n } from 'i18next'

import type { LazyBackendModule } from './lazy-backend'

function toLazyBackend(i18n: I18n): LazyBackendModule | undefined {
  // `services.backendConnector` is untyped in i18next and absent until init
  // runs, so the backend is duck-typed rather than asserted.
  const backend: unknown = i18n.services?.backendConnector?.backend

  if (typeof (backend as Partial<LazyBackendModule> | undefined)?.prefetch === 'function') {
    return backend as LazyBackendModule
  }

  return undefined
}

function toNamespaceList(namespaces: I18n['options']['ns']): readonly string[] {
  if (!namespaces) {
    return []
  }

  return typeof namespaces === 'string' ? [namespaces] : namespaces
}

/**
 * Warms the translation chunks for a language the user has not switched to.
 *
 * Namespaces come from `options.ns`, which i18next grows as routes ask for
 * namespaces, so this covers exactly what `reloadResources` re-reads on an
 * actual switch. Nothing is written into the i18next store: `reloadResources`
 * re-invokes the loaders regardless of what the store holds, so the win is the
 * warm module cache the second `import()` resolves from. Namespaces already in
 * the store were fetched once and are skipped for the same reason.
 *
 * i18next's own `loadLanguages` is deliberately avoided: it appends the language
 * to `options.preload`, which would make every later namespace load fetch a
 * second copy for a language the user may never pick.
 */
export async function prefetchTranslations(
  i18n: I18n | undefined,
  language: string,
): Promise<void> {
  const backend = i18n && toLazyBackend(i18n)

  if (!i18n || !backend) {
    return
  }

  const namespaces = toNamespaceList(i18n.options.ns).filter(
    namespace => !i18n.hasResourceBundle(language, namespace),
  )

  if (namespaces.length === 0) {
    return
  }

  await backend.prefetch(language, namespaces)
}
